import type { ComponentInstance } from 'vue';

export type GlobalHelpers = {
    generateLocalePath: <T extends string | undefined, R = T extends string ? string : undefined>(input: T, skipLocalPath?: boolean) => R;
};

export default defineNuxtPlugin((nuxtApp) => {
    const localePath = useLocalePathWoom();
    const route = useRoute();
    const { addPreview } = usePreview();

    const helpers: GlobalHelpers = {
        generateLocalePath: <T extends string | undefined, R = T extends string ? string : undefined>(input: T, skipLocalPath?: boolean): R => {
            if (typeof input !== 'string') return undefined as R;

            // don't generate a locale path if what we got was an absolute link [usually an external link]
            if (input.startsWith('http')) {
                return input as R;
            }
            if (input.startsWith('#')) {
                const queryEntries = Object.entries(route.query);
                const queryParams = queryEntries.length ? `?${queryEntries.map(([key, value]) => `${key}=${value}`).join('&')}` : '';
                return `${route.path}${queryParams}${input}` as R;
            }

            if (skipLocalPath) return addPreview(input) as R;

            const output = localePath(input);

            const inputFirstSegment = input.split('/').find((x) => x.length > 0);

            const outputFirstSegment = output.split('/').find((x) => x.length > 0);

            // if generated url has the same starting segment as our output, the locale path was already applied
            // so just return the input unchanged
            if (inputFirstSegment === outputFirstSegment) {
                if (import.meta.env.DEV && import.meta.client) {
                    const currentInstance = getCurrentInstance();
                    let parentPart = '';
                    const addParentPath = (parent: ComponentInstance<any> | null) => {
                        const componentName = parent?.type?.__name || parent?.type?.name;
                        const componentFile = parent?.type?.__file;

                        parentPart += `\nfrom component '${componentName}'`;
                        if (componentFile) parentPart += ` (${componentFile})`;
                        if (parent?.parent) addParentPath(parent?.parent);
                    };

                    addParentPath(currentInstance?.parent);

                    // eslint-disable-next-line no-console
                    console.warn(
                        `%c generateLocalePath just stopped you from applying locale path to a link twice${parentPart}`,
                        'background: #222; color: #bada55',
                        input,
                    );
                }
                return addPreview(input) as R;
            }

            // otherwise, return the new url with locale applied
            return addPreview(output) as R;
        },
    };

    return {
        provide: {
            helpers,
        },
    };
});
